import React from 'react';
import {View} from 'react-native';
import CCText from '@/Atoms/CCText';

type EvalBarProps = {
  // Gate rendering entirely (engine off / board not measured yet).
  visible: boolean;
  boardWidth: number;
  // handleCevalInfo's display payload: whitePovValue is the single
  // normalized-once value (centipawns, or signed mate-in-N when isMate) -
  // both the bar fill and the score text below are derived from it so they
  // can never disagree, regardless of board orientation.
  evalResult: {
    whitePovValue: number;
    isMate: boolean;
  } | null;
  flipped: boolean;
};

// The vertical eval bar next to the board, lichess-style: white's share of
// the bar follows the eval clamped to ±8 pawns, mate pins it to 100/0, and
// the numeric score sits on whichever side is winning. The board-orientation
// flip only ever affects which end of the bar the fill/text sit on
// (`displayPercentage`/`isTextOnWhite` below) - never the sign of the score
// itself, which stays white-POV no matter how the board is oriented.
function EvalBar({visible, boardWidth, evalResult, flipped}: EvalBarProps) {
  if (!visible) {
    return null;
  }

  let whitePercentage = 50;
  let scoreText = '0.00';
  if (evalResult) {
    const {whitePovValue, isMate} = evalResult;
    if (isMate) {
      whitePercentage = whitePovValue > 0 ? 100 : 0;
      scoreText = whitePovValue === 0 ? '#' : `M${Math.abs(whitePovValue)}#`;
    } else {
      const val = whitePovValue / 100;
      const clamped = Math.max(-8, Math.min(8, val));
      whitePercentage = ((clamped + 8) / 16) * 100;
      scoreText = val.toFixed(2);
    }
  }

  const displayPercentage = flipped ? 100 - whitePercentage : whitePercentage;

  const whiteIsBetter = whitePercentage > 50;
  const isTextOnWhite = flipped ? !whiteIsBetter : whiteIsBetter;

  return (
    <View
      style={{
        width: 20,
        height: boardWidth,
        backgroundColor: '#262322',
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.15)',
      }}>
      <View
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: flipped ? undefined : 0,
          top: flipped ? 0 : undefined,
          height: `${displayPercentage}%`,
          backgroundColor: '#FFFFFF',
        }}
      />
      <View
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: isTextOnWhite
            ? flipped
              ? 6
              : undefined
            : flipped
            ? undefined
            : 6,
          bottom: isTextOnWhite
            ? flipped
              ? undefined
              : 6
            : flipped
            ? 6
            : undefined,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CCText
          fontSize={9}
          fontWeight="bold"
          color={isTextOnWhite ? '#262322' : '#FFFFFF'}
          style={{
            textAlign: 'center',
          }}>
          {scoreText}
        </CCText>
      </View>
    </View>
  );
}

export default EvalBar;
